import type { ReactNode } from 'react';
import React, { useCallback, useMemo } from 'react';
import type { TypeDefaults } from '@components/ui/cards/TypeDefaultsPreview';
import { TypeDefaultsPreview } from '@components/ui/cards/TypeDefaultsPreview';
import { ResourceCatalog } from '@components/ui/catalog/ResourceCatalog';
import type {
  CatalogInteractionHandler,
  CatalogNotifier,
} from '@components/ui/catalog/ResourceCatalog/ResourceCatalog.interactions';
import type {
  CatalogRow,
  CatalogVerbs,
  ResourceCatalogProps,
} from '@components/ui/catalog/ResourceCatalog/ResourceCatalog.types';
// Type-only import via the direct /types path — never the feature barrel.
import type { FixtureType } from '@features/practice-cleanup/types';
import TuneIcon from '@mui/icons-material/Tune';
import { Box, Button } from '@mui/material';

import {
  APPOINTMENT_TYPE_CATALOG_LABELS,
  toCatalogEntries,
} from '../utils/catalogRows';

export interface AppointmentTypeCatalogProps {
  types: FixtureType[];
  verbs: CatalogVerbs;
  loading?: boolean;
  /** Fired after archive/restore settle — the archive/restore tracking seam. */
  onAction?: ResourceCatalogProps['onAction'];
  /**
   * Catalog interaction seam (filter/sort/expansion/undo). Statsig event
   * tracking lands with the route wiring (GDN-2335 phase 2): the host maps
   * these interactions to `useEventTracking` events there — this component
   * stays presentation-only and Storybook-safe (no provider requirements).
   */
  onInteraction?: CatalogInteractionHandler;
  /**
   * Opens the org appointment-type management modal (create/edit). The host
   * wires `useModal().open(MODAL_TYPES.APPOINTMENT_TYPES_MANAGEMENT)` in
   * phase 2; stories stub it. Omit to hide the button.
   */
  onManageTypes?: () => void;
  /** Verb-outcome toasts via the host's notistack; built-in fallback. */
  notifier?: CatalogNotifier;
}

/**
 * Appointment-type face of the generic ResourceCatalog (GDN-2335): adapts
 * the dry-run rows via toCatalogEntries and renders each row's expandable
 * detail with the shared TypeDefaultsPreview. Presentation-only — the host
 * wires the verbs (stories stub them with local state).
 */
export const AppointmentTypeCatalog: React.FC<AppointmentTypeCatalogProps> = ({
  types,
  verbs,
  loading,
  onAction,
  onInteraction,
  onManageTypes,
  notifier,
}) => {
  const entries = useMemo(() => toCatalogEntries(types), [types]);

  const rows = useMemo<CatalogRow[]>(
    () => entries.map((entry) => entry.row),
    [entries]
  );

  const defaultsById = useMemo<Map<string, TypeDefaults>>(
    () => new Map(entries.map((entry) => [entry.row.id, entry.defaults])),
    [entries]
  );

  const renderDetail = useCallback(
    (row: CatalogRow): ReactNode => {
      const defaults = defaultsById.get(row.id);
      if (!defaults) return null;
      // The detail row spans the full table width — the labeled "rows"
      // variant reads far better there than the one-line inline summary.
      return (
        <Box sx={{ px: 1, py: 0.5, maxWidth: 560 }}>
          <TypeDefaultsPreview variant="rows" defaults={defaults} />
        </Box>
      );
    },
    [defaultsById]
  );

  const toolbarAction = useMemo<ReactNode>(
    () =>
      onManageTypes ? (
        <Button
          variant="outlined"
          size="small"
          startIcon={<TuneIcon />}
          onClick={onManageTypes}
        >
          Manage appointment types
        </Button>
      ) : undefined,
    [onManageTypes]
  );

  return (
    <ResourceCatalog
      rows={rows}
      verbs={verbs}
      labels={APPOINTMENT_TYPE_CATALOG_LABELS}
      renderDetail={renderDetail}
      loading={loading}
      onAction={onAction}
      onInteraction={onInteraction}
      toolbarAction={toolbarAction}
      notifier={notifier}
    />
  );
};
